import { PayloadAction } from "@reduxjs/toolkit";
import { MaterialProposalType, ProposalReduxState } from "../types";
import { generateShortId } from "../../../../utils/library/shortId";
import { ProductCategory } from "../../../../gql/hooks";

export type AddMaterialToProposalType = {
  productId?: number;
} & Partial<MaterialProposalType>;

const AddMaterialToProposal = (
  state: ProposalReduxState,
  { payload }: PayloadAction<AddMaterialToProposalType>,
) => {
  const material = payload;

  state.materials.push({
    ...material,
    id: generateShortId(),
    order: state.materials?.length || 0,
  });
  state.isUpdated = true;
};

const AddMaterialsToProposal = (
  state: ProposalReduxState,
  {
    payload,
  }: PayloadAction<{
    materials: AddMaterialToProposalType[];
    reset?: boolean;
  }>,
) => {
  const { materials, reset } = payload;

  const newMaterials = materials.map(
    ({ productId, packagedProductId, category, custom }, index) => ({
      id: generateShortId(),
      order: (state.materials?.length || 0) + index,
      productId,
      packagedProductId,
      category,
      custom,
    }),
  );

  state.materials = reset
    ? newMaterials
    : [...state.materials, ...newMaterials];
  state.isUpdated = true;
};

const UpdateProposalMaterial = (
  state: ProposalReduxState,
  {
    payload,
  }: PayloadAction<{
    id: string;
    material: Partial<MaterialProposalType>;
  }>,
) => {
  const { id, material } = payload;

  const materialIndex = state.materials.findIndex(
    (material) => material.id === id,
  );
  if (materialIndex < 0) return;
  const stateMaterial = state.materials[materialIndex];

  let newMaterial = {
    ...stateMaterial,
    ...material,
    custom: {
      ...(stateMaterial?.custom || {}),
      ...(material.custom || {}),
    },
  };

  if (material.productId === -1) {
    newMaterial = {
      ...newMaterial,
      productId: undefined,
      packagedProductId: undefined,
    };
  }

  if (newMaterial.category !== ProductCategory.Custom) {
    newMaterial.custom = {};
  }

  state.materials[materialIndex] = newMaterial;
  state.isUpdated = true;
};

const RemoveMaterialsFromProposal = (
  state: ProposalReduxState,
  {
    payload,
  }: PayloadAction<{
    ids: string[];
  }>,
) => {
  state.materials = state.materials.filter(
    (material) => !payload.ids.includes(material.id),
  );
  state.isUpdated = true;
};

const RemoveMaterialsFromProposalByProductId = (
  state: ProposalReduxState,
  {
    payload,
  }: PayloadAction<{
    productIds: number[];
  }>,
) => {
  state.materials = state.materials.filter(
    (material) =>
      !!material.productId && !payload.productIds.includes(material.productId),
  );
  state.isUpdated = true;
};

// Update full Materials list
const UpdateProposalMaterials = (
  state: ProposalReduxState,
  {
    payload,
  }: PayloadAction<{
    materials: MaterialProposalType[];
  }>,
) => {
  const { materials } = payload;
  state.materials = materials;
  state.isUpdated = true;
};

const MaterialsReducer = {
  // Materials Specific
  AddMaterialToProposal,
  AddMaterialsToProposal,
  UpdateProposalMaterial,
  RemoveMaterialsFromProposal,

  // Materials General
  UpdateProposalMaterials,
  RemoveMaterialsFromProposalByProductId,
};

export default MaterialsReducer;
